// Plays a map's ambient_generic sounds: music and effects started by the map's I/O.
// Positional sounds fade out over the entity's radius around the listener; "play everywhere"
// sounds aren't positioned. Sound files are decoded lazily the first time they play.

import { soundKey } from '../maps/bsp/mapSounds';
import type { MapRuntime } from '../sim/mapRuntime';
import type { Sound } from './sound';

interface Source {
  key: string | null;
  everywhere: boolean;
  looped: boolean;
  radius: number;
  pos: [number, number, number];
  node: AudioBufferSourceNode | null;
  gain: GainNode | null;
  /** Bumped on every play/stop so a slow decode doesn't start a sound that was stopped meanwhile. */
  serial: number;
}

/** A sound this long is a song, not an effect or an ambient loop. */
const SONG_SECONDS = 25;
/** For a WAV whose header can't be read: this big is a song even at CD quality. */
const SONG_BYTES = 4 * 1024 * 1024;

/** Length of a WAV file in seconds from its header (data size / bytes per second), or null. */
export function wavSeconds(data: Uint8Array): number | null {
  if (data.byteLength < 12) return null;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const tag = (o: number) => String.fromCharCode(data[o], data[o + 1], data[o + 2], data[o + 3]);
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return null;
  let byteRate = 0;
  let dataBytes = 0;
  for (let o = 12; o + 8 <= data.byteLength;) {
    const size = dv.getUint32(o + 4, true);
    if (tag(o) === 'fmt ' && o + 16 <= data.byteLength) byteRate = dv.getUint32(o + 16, true);
    else if (tag(o) === 'data') dataBytes = Math.min(size, data.byteLength - o - 8);
    o += 8 + size + (size & 1);
  }
  return byteRate > 0 && dataBytes > 0 ? dataBytes / byteRate : null;
}

/**
 * Whether a map plays music of its own: one of its ambient_generic sounds that we have the file
 * for is an MP3, is named like music, or is long enough to be a song (low-quality WAVs are small, so
 * the length comes from the header, not the file size). (Music the map names but
 * doesn't include can't play, so it doesn't count.)
 */
export function mapHasMusic(messages: readonly string[], files: ReadonlyMap<string, Uint8Array>): boolean {
  for (const m of messages) {
    const key = soundKey(m);
    const data = key ? files.get(key) : undefined;
    if (!key || !data) continue;
    if (/\.mp3$|music|song|bgm|soundtrack|theme/.test(key)) return true;
    const seconds = wavSeconds(data);
    if (seconds === null ? data.byteLength >= SONG_BYTES : seconds >= SONG_SECONDS) return true;
  }
  return false;
}

export class MapAudio {
  private readonly files: Map<string, Uint8Array>;
  private readonly buffers = new Map<string, Promise<AudioBuffer | null>>();
  private readonly sources = new Map<number, Source>();
  /** Sounds that couldn't be decoded (some Source WAVs use ADPCM). */
  readonly failed = new Set<string>();
  /** The map brings its own music (then ours stays off). */
  readonly hasMusic: boolean;

  constructor(
    private readonly sound: Sound,
    private readonly runtime: MapRuntime,
    files: [string, Uint8Array][],
  ) {
    this.files = new Map(files);
    this.hasMusic = mapHasMusic(
      runtime.ents.filter((e) => e?.ambient).map((e) => e!.keys.message ?? ''),
      this.files,
    );
    for (const e of runtime.ents) {
      if (!e?.ambient) continue;
      const sf = Math.trunc(Number(e.keys.spawnflags) || 0);
      this.sources.set(e.index, {
        key: soundKey(e.keys.message ?? ''),
        everywhere: (sf & 1) !== 0,
        looped: e.ambient.looped,
        radius: Math.max(64, Number(e.keys.radius) || 1250),
        pos: [...e.spawn] as [number, number, number],
        node: null,
        gain: null,
        serial: 0,
      });
    }
  }

  private buffer(key: string, ctx: AudioContext): Promise<AudioBuffer | null> {
    let p = this.buffers.get(key);
    if (!p) {
      const data = this.files.get(key);
      p = data
        ? ctx.decodeAudioData(data.slice().buffer as ArrayBuffer).catch(() => {
            this.failed.add(key);
            return null;
          })
        : Promise.resolve(null);
      this.buffers.set(key, p);
    }
    return p;
  }

  private stop(s: Source) {
    s.serial++;
    try {
      s.node?.stop();
    } catch {
      // Already stopped.
    }
    s.node?.disconnect();
    s.node = null;
    s.gain = null;
  }

  private async play(s: Source, volume: number) {
    const ctx = this.sound.context;
    const out = this.sound.output;
    this.stop(s);
    if (!ctx || !out || !s.key) return;
    const serial = s.serial;
    const buf = await this.buffer(s.key, ctx);
    if (!buf || serial !== s.serial) return;
    const node = ctx.createBufferSource();
    node.buffer = buf;
    node.loop = s.looped;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    node.connect(gain);
    if (s.everywhere) gain.connect(out);
    else {
      const panner = ctx.createPanner();
      panner.panningModel = 'equalpower';
      panner.distanceModel = 'linear';
      panner.refDistance = s.radius * 0.15;
      panner.maxDistance = s.radius;
      panner.rolloffFactor = 1;
      panner.positionX.value = s.pos[0];
      panner.positionY.value = s.pos[1];
      panner.positionZ.value = s.pos[2];
      gain.connect(panner).connect(out);
    }
    node.onended = () => {
      if (s.node === node) s.node = null;
    };
    node.start();
    s.node = node;
    s.gain = gain;
  }

  /** Run once per frame: apply the runtime's sound events and move the listener to the eye. */
  update(eye: readonly number[], yaw: number, pitch: number) {
    const ev = this.runtime.soundEvents;
    for (let i = 0; i < ev.length; i++) {
      const e = ev[i];
      const s = this.sources.get(e.entity);
      if (!s) continue;
      if (e.action === 'play') void this.play(s, e.volume);
      else if (e.action === 'stop') this.stop(s);
      else if (s.gain) s.gain.gain.value = e.volume;
    }
    ev.length = 0;
    const ctx = this.sound.context;
    if (!ctx) return;
    const l = ctx.listener;
    const cp = Math.cos(pitch);
    if (l.positionX) {
      l.positionX.value = eye[0];
      l.positionY.value = eye[1];
      l.positionZ.value = eye[2];
      l.forwardX.value = Math.cos(yaw) * cp;
      l.forwardY.value = Math.sin(yaw) * cp;
      l.forwardZ.value = -Math.sin(pitch);
      l.upX.value = 0;
      l.upY.value = 0;
      l.upZ.value = 1;
    }
  }

  dispose() {
    for (const s of this.sources.values()) this.stop(s);
    this.runtime.soundEvents.length = 0;
  }
}
